import _ from 'lodash';
import $ from 'jquery';
import {AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios';
import Cookies from 'cookies-js';
import {ErrorType} from '../enums';
import {CookieConstant} from '../constants';
import EventHub from '../eventHub';
import LtCommon from '../../../types/lt/ltCommon';

const genericErrorMessage = 'Oops! Something went wrong! Please try again later.';

export default class ApiService implements LtCommon.IApiService {
	public loading = false;
	public errorMessage = '';
	public errors: string[] = [];
	protected axiosInstance: AxiosInstance;
	private loadingCount = 0;
	private readonly source: string;

	constructor(source: string, axiosInstance: AxiosInstance) {
		this.source = source;
		this.axiosInstance = axiosInstance;
		this.axiosInstance.interceptors.request.use((config: AxiosRequestConfig) => {
			this.updateLoading(true, (config as LtCommon.IAxiosRequestConfig).ignoreLoading);

			const ssoId = Cookies.get(CookieConstant.ssoId);
			if (ssoId) {
				config.headers = $.extend({}, config.headers, { 'x-ltf-ssoid': ssoId });
			}

			const profileToken = Cookies.get(CookieConstant.profileToken);
			if (profileToken) {
				config.headers = $.extend({}, config.headers, { 'x-ltf-profile': profileToken });
			}

			return config;
		}, (error: AxiosError) => {
			let errorMessage = genericErrorMessage;
			let ignoreLoading = false;
			let suppressError = false;
			let url = '';
			let success = false;
			let suppressErrorCodes: number[] = [];
			let statusCode = 0;

			if (error) {
				if (error.response && error.response.status === 401) {
					errorMessage = '';
					window.location.assign(window.lt.globalPageDrivers.global.loginResource);

					return Promise.reject(error);
				}

				if (error.config) {
					ignoreLoading = (error.config as LtCommon.IAxiosRequestConfig).ignoreLoading ? (error.config as LtCommon.IAxiosRequestConfig).ignoreLoading : false;
					suppressError = (error.config as LtCommon.IAxiosRequestConfig).suppressError ? (error.config as LtCommon.IAxiosRequestConfig).suppressError : false;
					suppressErrorCodes = (error.config as LtCommon.IAxiosRequestConfig).suppressErrorCodes ? (error.config as LtCommon.IAxiosRequestConfig).suppressErrorCodes : [];
					url = error.config.url as string;
				}

				if (error.message) {
					if (error.message.indexOf('Ignore Loading') !== -1) {
						errorMessage = '';
						ignoreLoading = true;
						success = true;
					} else if (error.message.indexOf('Request Canceled') !== -1) {
						errorMessage = '';
						suppressError = true;
						success = true;
					}
				}

				if (error.response) {
					statusCode = error.response.status;
				}
			}

			this.updateError(url, errorMessage, suppressError, suppressErrorCodes, statusCode, success);
			this.updateLoading(false, ignoreLoading);

			return Promise.reject(error);
		});

		this.axiosInstance.interceptors.response.use((response: AxiosResponse) => {
			const suppressErrorCodes = (response.config as LtCommon.IAxiosRequestConfig).suppressErrorCodes ? (response.config as LtCommon.IAxiosRequestConfig).suppressErrorCodes : [];

			this.updateError(response.config.url!, '', (response.config as LtCommon.IAxiosRequestConfig).suppressError, suppressErrorCodes, response.status, true);
			this.updateLoading(false, (response.config as LtCommon.IAxiosRequestConfig).ignoreLoading);

			return response;
		}, (error: AxiosError) => {
			let errorMessage = genericErrorMessage;
			let ignoreLoading = false;
			let suppressError = false;
			let suppressErrorCodes: number[] = [];
			let statusCode = 0;
			let url = '';
			let success = false;

			if (error) {
				if (error.config) {
					ignoreLoading = (error.config as LtCommon.IAxiosRequestConfig).ignoreLoading ? (error.config as LtCommon.IAxiosRequestConfig).ignoreLoading : false;
					suppressError = (error.config as LtCommon.IAxiosRequestConfig).suppressError ? (error.config as LtCommon.IAxiosRequestConfig).suppressError : false;
					suppressErrorCodes = (error.config as LtCommon.IAxiosRequestConfig).suppressErrorCodes ? (error.config as LtCommon.IAxiosRequestConfig).suppressErrorCodes : [];
					url = error.config.url as string;
				}

				if (error.message) {
					if (error.message.indexOf('Ignore Loading') !== -1) {
						errorMessage = '';
						ignoreLoading = true;
						success = true;
					} else if (error.message.indexOf('Request Canceled') !== -1) {
						errorMessage = '';
						suppressError = true;
						success = true;
					}
				}

				if (error.response) {
					statusCode = error.response.status;
					if (statusCode === 401) {
						errorMessage = '';
						window.location.assign(window.lt.globalPageDrivers.global.loginResource);

						return Promise.reject(error);
					}

					if ((statusCode === 400 && error.response.data.errorType === ErrorType.DuplicateEmailAddress) || error.response.data.errorType === ErrorType.InvalidTrainerizeProgram || error.response.data.errorType === ErrorType.MissingMemberEmail) {
						errorMessage = '';
						suppressError = true;
					}
				}
			}

			this.updateError(url, errorMessage, suppressError, suppressErrorCodes, statusCode, success);
			this.updateLoading(false, ignoreLoading);

			return Promise.reject(error);
		});
	}

	public updateError(url: string, errorMessage: string, suppressError: boolean, suppressErrorCodes: number[], status: number, success: boolean) {
		const matchedErrorCode = suppressError && (suppressErrorCodes.length === 0 || _.indexOf(suppressErrorCodes, status) > -1);

		if (success) {
			this.errors = _.without(this.errors, url);
		} else if (!matchedErrorCode) {
			this.errors.push(url);
		}

		if (this.errors.length > 0 || errorMessage) {
			if (errorMessage && !suppressError) {
				this.errorMessage = errorMessage;
			}
		} else {
			this.errorMessage = '';
		}
	}

	private updateLoading(increment: boolean, ignoreLoading?: boolean): void {
		if (!ignoreLoading) {
			this.loadingCount += increment ? 1 : -1;
			this.loading = this.loadingCount > 0;
		}

		EventHub.$emit(this.source + '-loading', this.loading);
	}
}
